import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import SearchSelect from '../components/SearchSelect';
import { dt, day, num, apiError } from '../utils/format';
import { packsOf } from '../utils/packs';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import { useDataTable, TableToolbar, TablePager } from '../components/DataTable';

const statusBadge = (s) => ({ requested: 'badge-blue', partial: 'badge-gold', created: 'badge-gray', dispatched: 'badge-gold', received: 'badge-blue', closed: 'badge-green', cancelled: 'badge-red' }[s] || 'badge-gray');
const inTransitTotal = (t) => num((t.items || []).reduce((s, it) => s + Math.max(0, Number(it.qtyFulfilled || 0) - Number(it.qtyReceived || 0)), 0));
const FULFILMENT = {
  ready: { label: 'Ready to dispatch', cls: 'badge-green' },
  partial: { label: 'Partly ready', cls: 'badge-gold' },
  production: { label: 'In production', cls: 'badge-gold' },
  waiting: { label: 'Waiting in warehouse', cls: 'badge-red' },
};
const useVariantOptions = (products) => useMemo(() =>
  products.flatMap((p) => packsOf(p).map((v) => ({ id: v.id, productId: p.id, label: `${p.name} — ${v.weightLabel}`, sub: `#${p.id}`, search: `${p.name} ${p.code || ''} ${p.id} ${v.weightLabel} ${v.barcode || ''} ${v.sku || ''}` }))), [products]);

// ── Push transfer: source sends stock out (with availability guard) ──
const NewTransferModal = ({ products, locations, onClose, onSaved }) => {
  const [fromLocationId, setFrom] = useState('');
  const [toLocationId, setTo] = useState('');
  const [lines, setLines] = useState([{ variantId: '', qtySent: 1 }]);
  const [stock, setStock] = useState(null);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const variantOptions = useVariantOptions(products);

  useEffect(() => {
    if (!fromLocationId) { setStock(null); return; }
    api.get(`/inventory/levels?itemType=product&locationId=${fromLocationId}`)
      .then(({ data }) => { const m = {}; for (const l of data.levels || []) if (l.variantId) m[l.variantId] = (m[l.variantId] || 0) + Number(l.quantity); setStock(m); })
      .catch(() => setStock({}));
  }, [fromLocationId]);

  const availOf = (variantId) => (stock && variantId ? Number(stock[Number(variantId)] || 0) : null);
  const lineOver = (l) => l.variantId && stock && Number(l.qtySent) > availOf(l.variantId);
  const canSubmit = fromLocationId && toLocationId && fromLocationId !== toLocationId
    && lines.some((l) => l.variantId && Number(l.qtySent) > 0) && !lines.some(lineOver);

  const setLine = (i, k, v) => setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const submit = async () => {
    setBusy(true); setError('');
    try {
      const items = lines.filter((l) => l.variantId && Number(l.qtySent) > 0).map((l) => {
        const opt = variantOptions.find((o) => o.id === Number(l.variantId));
        return { itemType: 'product', itemId: opt.productId, variantId: opt.id, qtySent: Number(l.qtySent), unit: 'unit' };
      });
      if (!items.length) throw new Error('Add at least one item');
      if (lines.some(lineOver)) throw new Error('One or more items exceed the quantity available at the source location');
      const { data } = await api.post('/transfers', { fromLocationId: Number(fromLocationId), toLocationId: Number(toLocationId), items });
      toast.success('Transfer created'); onSaved(data.transfer); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="New Transfer" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy || !canSubmit}>{busy ? 'Saving…' : 'Create'}</button></>}>
      <div className="row">
        <div className="field"><label>From</label><select value={fromLocationId} onChange={(e) => setFrom(e.target.value)}><option value="">— select —</option>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
        <div className="field"><label>To</label><select value={toLocationId} onChange={(e) => setTo(e.target.value)}><option value="">— select —</option>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
      </div>
      {fromLocationId && toLocationId === fromLocationId && <p className="form-error">Source and destination must be different.</p>}
      <div className="section-head" style={{ marginTop: '.4rem' }}><h3 style={{ fontSize: '.95rem' }}>Products</h3><button className="btn btn-outline btn-sm" onClick={() => setLines((p) => [...p, { variantId: '', qtySent: 1 }])}>+ Add</button></div>
      {/* overflow:visible so the SearchSelect dropdown isn't clipped by table-wrap's auto overflow */}
      <div className="table-wrap" style={{ overflow: 'visible' }}>
        <table className="grid">
          <thead><tr><th>Product / variant</th><th>Available</th><th>Qty</th><th></th></tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const avail = availOf(l.variantId); const over = lineOver(l);
              return (
                <tr key={i}>
                  <td><SearchSelect placeholder="Search product by name or ID…" value={l.variantId} onChange={(v) => setLine(i, 'variantId', v)} options={variantOptions.map((o) => ({ value: o.id, label: o.label, sub: o.sub, search: o.search }))} /></td>
                  <td className="muted">{!fromLocationId ? '—' : l.variantId ? `${num(avail)} in stock` : '—'}</td>
                  <td>
                    <input type="number" min="0" style={{ width: 90, borderColor: over ? 'var(--danger)' : undefined }} value={l.qtySent} onChange={(e) => setLine(i, 'qtySent', e.target.value)} />
                    {over && <div className="neg" style={{ fontSize: '.75rem' }}>only {num(avail)} available</div>}
                  </td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>✕</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!fromLocationId && <p className="muted small">Select a source location to see available quantities.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── Stock request: a branch asks the store for stock ──
const RequestModal = ({ products, locations, onClose, onSaved }) => {
  const { user, can } = useAuth();
  // All branch requests are raised on the warehouse; the warehouse consolidates & procures.
  const mainStore = locations.find((l) => l.type === 'store');
  const fromLocationId = mainStore ? String(mainStore.id) : '';
  // A branch requester can only request for their OWN branch (auto-selected, locked).
  // Only warehouse/admin (transfers:write) may choose which branch to deliver to.
  const chooses = can('transfers:write');
  const homeId = user?.locationId ? String(user.locationId) : '';
  const [toLocationId, setTo] = useState(chooses ? '' : homeId);         // branch / destination
  const [lines, setLines] = useState([{ key: '', qtyRequested: 1 }]);
  const [needByDate, setNeedByDate] = useState('');
  const [stock, setStock] = useState(null);        // option key -> available at the requesting branch
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  // Requestable items: the product's BULK/loose form (variantId 0) plus one option
  // per pack. A branch can request bulk even when the product also has packs.
  const options = useMemo(() => products.flatMap((p) => {
    const vs = packsOf(p);   // none for Loose products, so they only offer the bulk line
    // The base form is shown as the product's unit (KG/L), not "loose". When the
    // product also has packs, "KGS" next to "1KG" reads as the same thing — so the
    // bulk line says "Bulk, by KGS" and every pack line ends in "pack".
    const u = p.unit || 'kg';
    const baseLabel = vs.length ? `${p.name} — Bulk, by ${u}` : `${p.name} (${u})`;
    const bulk = { key: `p${p.id}`, productId: p.id, variantId: 0, unit: u, label: baseLabel, sub: vs.length ? `bulk · #${p.id}` : `#${p.id}`, search: `${p.name} ${p.code || ''} ${p.id} ${u} bulk loose` };
    return [bulk, ...vs.map((v) => ({ key: `v${v.id}`, productId: p.id, variantId: v.id, unit: 'unit', label: `${p.name} — ${v.weightLabel} pack`, sub: `pack · #${p.id}`, search: `${p.name} ${p.code || ''} ${p.id} ${v.weightLabel} pack ${v.barcode || ''} ${v.sku || ''}` }))];
  }), [products]);
  const optByKey = useMemo(() => Object.fromEntries(options.map((o) => [o.key, o])), [options]);

  // Show what the requesting branch already has on hand, so they request the top-up.
  useEffect(() => {
    if (!toLocationId) { setStock(null); return; }
    api.get(`/inventory/levels?itemType=product&locationId=${toLocationId}`)
      .then(({ data }) => { const m = {}; for (const l of data.levels || []) { const k = Number(l.variantId) > 0 ? `v${l.variantId}` : `p${l.itemId}`; m[k] = (m[k] || 0) + Number(l.quantity); } setStock(m); })
      .catch(() => setStock({}));
  }, [toLocationId]);
  const availOf = (key) => (stock && key ? Number(stock[key] || 0) : null);

  const setLine = (i, k, v) => setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const canSubmit = fromLocationId && toLocationId && fromLocationId !== toLocationId && lines.some((l) => l.key && Number(l.qtyRequested) > 0);
  const submit = async () => {
    setBusy(true); setError('');
    try {
      const items = lines.filter((l) => l.key && Number(l.qtyRequested) > 0).map((l) => {
        const opt = optByKey[l.key];
        return { itemType: 'product', itemId: opt.productId, variantId: opt.variantId, qtyRequested: Number(l.qtyRequested), unit: opt.unit };
      });
      if (!items.length) throw new Error('Add at least one item');
      const { data } = await api.post('/transfers/request', { fromLocationId: Number(fromLocationId), toLocationId: Number(toLocationId), needByDate: needByDate || undefined, items });
      toast.success('Request sent to store'); onSaved(data.transfer); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Request stock from ${mainStore?.name || 'Main Store (Warehouse)'}`} onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy || !canSubmit}>{busy ? 'Sending…' : 'Send request'}</button></>}>
      {chooses && (
        <div className="field" style={{ maxWidth: 300 }}><label>Deliver to (branch)</label>
          <select value={toLocationId} onChange={(e) => setTo(e.target.value)}><option value="">— select branch —</option>{locations.filter((l) => String(l.id) !== fromLocationId).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        </div>
      )}
      {!chooses && !homeId && <p className="form-error">Your account has no branch assigned — ask an admin to set your branch before requesting.</p>}
      {fromLocationId && String(toLocationId) === String(fromLocationId) && <p className="form-error">Store and branch must be different.</p>}
      <div className="field" style={{ maxWidth: 220 }}><label>Needed by</label><input type="date" value={needByDate} onChange={(e) => setNeedByDate(e.target.value)} /></div>
      <div className="section-head" style={{ marginTop: '.4rem' }}><h3 style={{ fontSize: '.95rem' }}>Products</h3><button className="btn btn-outline btn-sm" onClick={() => setLines((p) => [...p, { key: '', qtyRequested: 1 }])}>+ Add</button></div>
      {/* overflow:visible so the SearchSelect dropdown isn't clipped by table-wrap's auto overflow */}
      <div className="table-wrap" style={{ overflow: 'visible' }}>
        <table className="grid">
          <thead><tr><th>Product / pack</th><th>Available Qty</th><th>Requested qty</th><th></th></tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const opt = optByKey[l.key];
              const avail = availOf(l.key);
              return (
                <tr key={i}>
                  <td><SearchSelect placeholder="Search product by name or ID…" value={l.key} onChange={(v) => setLine(i, 'key', v)} options={options.map((o) => ({ value: o.key, label: o.label, sub: o.sub, search: o.search }))} /></td>
                  <td className="muted">{!toLocationId ? '—' : l.key ? `${num(avail)} ${opt ? (opt.variantId === 0 ? (opt.unit || 'kg') : 'packs') : ''} on hand` : '—'}</td>
                  <td>
                    <div className="flex" style={{ gap: 6, alignItems: 'center' }}>
                      <input type="number" min="0" step="0.001" style={{ width: 100 }} value={l.qtyRequested} onChange={(e) => setLine(i, 'qtyRequested', e.target.value)} />
                      {opt && <span className="muted small">{opt.variantId === 0 ? (opt.unit || 'kg') : 'packs'}</span>}
                    </div>
                  </td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>✕</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="muted small">The store can adjust these quantities before dispatching.</p>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── Detail: requests use partial fulfilment (stays open until fully delivered);
//    push transfers use dispatch → receive → close ──
const DetailModal = ({ transfer, itemLabel, onClose, onChanged, can }) => {
  const { user } = useAuth();
  const [t, setT] = useState(transfer);
  const isRequest = t.kind === 'request';
  const inProd = (it) => Number(it.qtyToKitchen || 0);   // being made in the kitchen
  const toDispatch = (it) => Math.max(0, Number(it.qtyRequested || 0) - Number(it.qtyFulfilled || 0) - inProd(it));   // at-warehouse portion still to send
  const inTransit = (it) => Math.max(0, Number(it.qtyFulfilled || 0) - Number(it.qtyReceived || 0));       // sent, awaiting branch accept

  const [recv, setRecv] = useState(() => Object.fromEntries((transfer.items || []).map((it) => [it.id, { qtyReceived: it.qtySent, damageQty: 0 }])));
  const [fulfil, setFulfil] = useState(() => Object.fromEntries((transfer.items || []).map((it) => [it.id, toDispatch(it)])));
  const [accept, setAccept] = useState(() => Object.fromEntries((transfer.items || []).map((it) => [it.id, inTransit(it)])));
  const [srcStock, setSrcStock] = useState(null);
  const [busy, setBusy] = useState(false);

  const hasToDispatch = (t.items || []).some((it) => toDispatch(it) > 0);
  const hasInTransit = (t.items || []).some((it) => inTransit(it) > 0);
  // Fulfilling a branch request and dispatching a manual transfer are separately
  // grantable, so access can be restricted per role (transfers:write covers both).
  const canFulfilReq = can('transfers:write') || can('transfers:fulfil');
  const canDispatchTransfer = can('transfers:write') || can('transfers:dispatch');
  const canFulfil = isRequest && ['requested', 'partial', 'dispatched'].includes(t.status) && canFulfilReq && hasToDispatch;
  // A delivery is accepted by the RECEIVING branch only (the stock lands there);
  // transfers:write is an admin/manager override.
  const atDestination = user?.locationId && String(user.locationId) === String(t.toLocationId);
  const canAccept = isRequest && ['partial', 'dispatched'].includes(t.status) && can('transfers:receive') && hasInTransit && (atDestination || can('transfers:write'));
  const showStock = canFulfil || (!isRequest && t.status === 'created');

  useEffect(() => {
    if (!showStock) return;
    api.get(`/inventory/levels?itemType=product&locationId=${t.fromLocationId}`)
      .then(({ data }) => { const m = {}; for (const l of data.levels || []) { const k = Number(l.variantId) > 0 ? `v${l.variantId}` : `p${l.itemId}`; m[k] = (m[k] || 0) + Number(l.quantity); } setSrcStock(m); })
      .catch(() => setSrcStock({}));
  }, [showStock, t.fromLocationId]);
  // After a dispatch/accept the outstanding shrinks — reset inputs to what's left.
  useEffect(() => {
    setFulfil(Object.fromEntries((t.items || []).map((it) => [it.id, toDispatch(it)])));
    setAccept(Object.fromEntries((t.items || []).map((it) => [it.id, inTransit(it)])));
  }, [t]);   // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, body) => {
    setBusy(true);
    try { const { data } = await api.post(`/transfers/${t.id}/${path}`, body); setT(data.transfer); onChanged(data.transfer); toast.success(`${isRequest ? 'Request' : 'Transfer'} ${data.transfer.status}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const setR = (id, k, v) => setRecv((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  const setF = (id, v) => setFulfil((p) => ({ ...p, [id]: v }));
  const setA = (id, v) => setAccept((p) => ({ ...p, [id]: v }));
  const availOf = (it) => (srcStock ? Number(srcStock[it.variantId ? `v${it.variantId}` : `p${it.itemId}`] || 0) : null);
  const bulkOf = (it) => (srcStock ? Number(srcStock[`p${it.itemId}`] || 0) : 0);   // loose bulk of the same product
  const label = (it) => (it.variantId ? itemLabel[`v${it.variantId}`] : itemLabel[`p${it.itemId}`]) || `#${it.itemId}`;
  // Can't dispatch stock the warehouse doesn't hold yet (e.g. still in production).
  const overStock = !!srcStock && (t.items || []).some((it) => Number(fulfil[it.id] || 0) > (availOf(it) || 0) + 1e-9);
  const noStockToDispatch = !!srcStock && hasToDispatch && (t.items || []).every((it) => toDispatch(it) <= 0 || (availOf(it) || 0) <= 0);
  // The requested PACK is empty but loose bulk of the same product IS in the warehouse
  // → the missing step is packing bulk into that pack, not production.
  const needsPacking = noStockToDispatch && (t.items || []).some((it) => it.variantId && toDispatch(it) > 0 && (availOf(it) || 0) <= 0 && bulkOf(it) > 0);

  const receiveBody = { items: Object.entries(recv).map(([id, v]) => ({ id: Number(id), qtyReceived: Number(v.qtyReceived), damageQty: Number(v.damageQty) })) };
  const fulfilBody = { items: (t.items || []).map((it) => ({ id: it.id, qty: Number(fulfil[it.id]) || 0 })).filter((x) => x.qty > 0) };
  // Weigh the requested packs out of loose stock instead of packing them (sweets etc.).
  const looseFulfilBody = { items: (t.items || []).filter((it) => it.variantId && toDispatch(it) > 0 && bulkOf(it) > 0).map((it) => ({ id: it.id, qty: toDispatch(it), fromLoose: true })) };
  const acceptBody = { items: (t.items || []).map((it) => ({ id: it.id, qty: Number(accept[it.id]) || 0 })).filter((x) => x.qty > 0) };

  return (
    <Modal title={`${isRequest ? 'Request' : 'Transfer'} ${t.code}`} onClose={onClose} wide
      footer={<>
        <button className="btn btn-outline" onClick={onClose}>Close</button>
        {canFulfil && <button className="btn btn-primary" onClick={() => act('fulfil', fulfilBody)} disabled={busy || !fulfilBody.items.length || overStock || noStockToDispatch}>{busy ? 'Dispatching…' : 'Dispatch now'}</button>}
        {canAccept && <button className="btn btn-gold" onClick={() => act('accept', acceptBody)} disabled={busy || !acceptBody.items.length}>{busy ? 'Accepting…' : 'Accept delivery'}</button>}
        {!isRequest && t.status === 'created' && canDispatchTransfer && <button className="btn btn-primary" onClick={() => act('dispatch')} disabled={busy}>Dispatch</button>}
        {!isRequest && t.status === 'dispatched' && can('transfers:receive') && <button className="btn btn-gold" onClick={() => act('receive', receiveBody)} disabled={busy}>Accept &amp; Receive</button>}
        {!isRequest && t.status === 'received' && (canFulfilReq || can('transfers:receive')) && <button className="btn btn-primary" onClick={() => act('close')} disabled={busy}>Close</button>}
      </>}>
      <div className="flex" style={{ justifyContent: 'space-between' }}>
        <div><strong>{t.fromLocation?.name}</strong> → <strong>{t.toLocation?.name}</strong>{isRequest && <span className="badge badge-blue" style={{ marginLeft: 8 }}>request</span>}</div>
        <span className={`badge ${statusBadge(t.status)}`}>{t.status}</span>
      </div>
      {isRequest && t.needByDate && <p className="muted small mt">Needed by {day(t.needByDate)}</p>}
      {canFulfil && !noStockToDispatch && <p className="muted small mt">Dispatch what {t.fromLocation?.name} can send now — it leaves the warehouse and the branch must accept it before branch stock updates.</p>}
      {canFulfil && needsPacking && (
        <div className="form-error mt">
          The requested pack size isn't packed, but there's stock by weight in {t.fromLocation?.name}. Weigh it out, or <Link to="/packing">pack it into these packs</Link> first.
          <div style={{ marginTop: '.5rem' }}>
            <button className="btn btn-gold btn-sm" onClick={() => act('fulfil', looseFulfilBody)} disabled={busy || !looseFulfilBody.items.length}>{busy ? 'Dispatching…' : 'Weigh out & dispatch'}</button>
          </div>
        </div>
      )}
      {canFulfil && noStockToDispatch && !needsPacking && <p className="form-error mt">These items aren't in the warehouse yet (still in production / awaiting acceptance) — you can't dispatch until stock is available.</p>}
      {canAccept && <p className="muted small mt">{inTransitTotal(t)} in transit — accept to add it to {t.toLocation?.name}'s stock.</p>}
      {isRequest && !canFulfilReq && !canAccept && ['requested', 'partial', 'dispatched'].includes(t.status) && <p className="muted small mt">Waiting for {t.fromLocation?.name} to dispatch this request.</p>}

      {isRequest ? (
        <div className="table-wrap mt">
          <table className="grid">
            <thead><tr>
              <th>Product</th><th className="right">Requested</th><th className="right">In production</th><th className="right">Dispatched</th><th className="right">Received</th>
              {canFulfil && <><th className="right">In stock</th><th className="right">Dispatch now</th></>}
              {canAccept && <th className="right">Accept now</th>}
              {!canFulfil && !canAccept && <th className="right">At warehouse</th>}
            </tr></thead>
            <tbody>
              {(t.items || []).map((it) => (
                <tr key={it.id}>
                  <td>{label(it)}</td>
                  <td className="right">{num(it.qtyRequested)}</td>
                  <td className="right">{inProd(it) > 0 ? <span className="badge badge-gold">{num(inProd(it))}</span> : '—'}</td>
                  <td className="right">{num(it.qtyFulfilled)}</td>
                  <td className="right">{num(it.qtyReceived)}</td>
                  {canFulfil && (
                    <>
                      <td className="right">{toDispatch(it) > 0 ? (srcStock ? num(availOf(it)) : '—') : ''}</td>
                      <td className="right">
                        {toDispatch(it) > 0 ? (
                          <>
                            <input type="number" min="0" style={{ width: 90 }} value={fulfil[it.id]} onChange={(e) => setF(it.id, e.target.value)} />
                            {Number(fulfil[it.id]) > toDispatch(it) && <div className="neg" style={{ fontSize: '.75rem' }}>over outstanding ({num(toDispatch(it))})</div>}
                            {srcStock && Number(fulfil[it.id]) > (availOf(it) || 0) && <div className="neg" style={{ fontSize: '.75rem' }}>over stock</div>}
                          </>
                        ) : (
                          <span className="badge badge-green" title="Nothing left to dispatch for this item">🔒 {Number(it.qtyFulfilled) > 0 ? 'dispatched' : 'done'}</span>
                        )}
                      </td>
                    </>
                  )}
                  {canAccept && (
                    <td className="right">
                      <input type="number" min="0" style={{ width: 90 }} value={accept[it.id]} onChange={(e) => setA(it.id, e.target.value)} />
                      {Number(accept[it.id]) > inTransit(it) && <div className="neg" style={{ fontSize: '.75rem' }}>over in-transit ({num(inTransit(it))})</div>}
                    </td>
                  )}
                  {!canFulfil && !canAccept && <td className="right">{num(toDispatch(it))}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap mt">
          <table className="grid">
            <thead><tr>
              <th>Product</th><th className="right">Sent</th>
              {['dispatched', 'received', 'closed'].includes(t.status) && <><th className="right">Received</th><th className="right">Damaged</th></>}
            </tr></thead>
            <tbody>
              {(t.items || []).map((it) => (
                <tr key={it.id}>
                  <td>{label(it)}</td>
                  <td className="right">{num(it.qtySent)}</td>
                  {t.status === 'dispatched' ? (
                    <>
                      <td className="right"><input type="number" style={{ width: 80 }} value={recv[it.id]?.qtyReceived} onChange={(e) => setR(it.id, 'qtyReceived', e.target.value)} /></td>
                      <td className="right"><input type="number" style={{ width: 80 }} value={recv[it.id]?.damageQty} onChange={(e) => setR(it.id, 'damageQty', e.target.value)} /></td>
                    </>
                  ) : ['received', 'closed'].includes(t.status) ? (
                    <><td className="right">{num(it.qtyReceived)}</td><td className="right">{num(it.damageQty)}</td></>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

// ── Dispatch console: pick many request lines (product + stock-request level) across
//    branches, filter by branch, and dispatch them all to their branches at once ──
const DispatchConsole = ({ onClose, onDone }) => {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [lines, setLines] = useState([]);
  const [sel, setSel] = useState({});       // key -> qty (string)
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const keyOf = (l) => `${l.transferId}:${l.requestItemId}`;

  const load = () => {
    setLoading(true);
    api.get(`/transfers/dispatch-board${branchId ? `?branchId=${branchId}` : ''}`).then(({ data }) => {
      setBranches(data.branches || []);
      setLines(data.lines || []);
      const s = {};
      (data.lines || []).forEach((l) => { if (l.canDispatch > 0) s[`${l.transferId}:${l.requestItemId}`] = String(l.canDispatch); });
      setSel(s);
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (l) => setSel((p) => { const k = keyOf(l); const n = { ...p }; if (k in n) delete n[k]; else n[k] = String(l.canDispatch || ''); return n; });
  const setQty = (l, v) => setSel((p) => ({ ...p, [keyOf(l)]: v }));
  const chosen = lines.filter((l) => (keyOf(l) in sel) && Number(sel[keyOf(l)]) > 0);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = chosen.map((l) => ({ transferId: l.transferId, itemId: l.requestItemId, qty: Number(sel[keyOf(l)]) }));
      const { data } = await api.post('/transfers/dispatch-bulk', { lines: payload });
      toast.success(`Dispatched ${data.result.dispatchedLines} line(s) across ${data.result.requests} request(s)`);
      onDone?.(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Dispatch to branch" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Close</button><button className="btn btn-primary" onClick={submit} disabled={busy || !chosen.length}>{busy ? 'Dispatching…' : `Dispatch ${chosen.length} line(s)`}</button></>}>
      <div className="flex gap" style={{ alignItems: 'center', marginBottom: '.6rem' }}>
        <label className="muted small">Branch</label>
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={{ minWidth: 200 }}>
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <span className="muted small">{lines.length} open line(s) · {chosen.length} selected</span>
      </div>
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap" style={{ maxHeight: 460, overflow: 'auto' }}>
          <table className="grid">
            <thead><tr><th></th><th>Branch</th><th>Request</th><th>Product</th><th>Pack</th><th className="right">Requested</th><th className="right">To dispatch</th><th className="right">Available</th><th className="right" style={{ width: 110 }}>Dispatch</th><th className="right">Need by</th></tr></thead>
            <tbody>
              {lines.map((l) => {
                const k = keyOf(l); const on = k in sel; const none = l.canDispatch <= 0;
                return (
                  <tr key={k}>
                    <td><input type="checkbox" checked={on} disabled={none} onChange={() => toggle(l)} /></td>
                    <td>{l.branch}</td>
                    <td className="mono muted">{l.transferCode}</td>
                    <td>{l.product} {l.urgent && <span className="badge badge-red">urgent</span>}</td>
                    <td className="muted">{l.variant}</td>
                    <td className="right">{num(l.requested)}</td>
                    <td className="right">{num(l.toDispatch)}{l.inProduction > 0 && <span className="muted small"> (+{num(l.inProduction)} making)</span>}</td>
                    <td className={`right ${none ? 'neg' : ''}`}>{num(l.available)}</td>
                    <td className="right"><input type="number" min="0" max={l.canDispatch} step="0.001" value={on ? sel[k] : ''} disabled={none} onChange={(e) => setQty(l, e.target.value)} style={{ width: 90 }} /></td>
                    <td className="muted right">{l.needByDate ? day(l.needByDate) : '—'}</td>
                  </tr>
                );
              })}
              {!lines.length && <tr><td colSpan={10} className="empty">No open request lines to dispatch{branchId ? ' for this branch' : ''}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

// Shared list — `mode` is 'transfers' (push) or 'requests' (branch stock requests).
const TransferList = ({ mode }) => {
  const { can } = useAuth();
  const { locationId: locFilter } = useLocationFilter();
  const isReq = mode === 'requests';
  const [transfers, setTransfers] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [console_, setConsole] = useState(false);
  const [tab, setTab] = useState('open');     // open | closed | all — open is what still needs doing
  const [view, setView] = useState('docs');   // docs = a row per document; items = a row per product
  const canFulfilReq = can('transfers:write') || can('transfers:fulfil');

  const load = () => { setLoading(true); api.get('/transfers').then(({ data }) => setTransfers(data.transfers || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get('/products').then(({ data }) => setProducts(data.products || [])); api.get('/locations').then(({ data }) => setLocations(data.locations || [])); }, []);
  const upsert = (t) => setTransfers((prev) => { const i = prev.findIndex((x) => x.id === t.id); return i >= 0 ? prev.map((x) => (x.id === t.id ? t : x)) : [t, ...prev]; });

  // Item labels keyed by variant (v<id>) and by product for loose items (p<productId>).
  const itemLabel = useMemo(() => {
    const m = {};
    for (const p of products) {
      m[`p${p.id}`] = `${p.name}${(p.variants || []).length ? '' : ` (${p.unit || 'kg'})`}`;
      for (const v of p.variants || []) m[`v${v.id}`] = `${p.name} — ${v.weightLabel}`;
    }
    return m;
  }, [products]);

  const all = transfers
    .filter((t) => (isReq ? t.kind === 'request' : t.kind !== 'request'))
    .filter((t) => !locFilter || String(t.toLocationId) === String(locFilter) || String(t.fromLocationId) === String(locFilter));
  // Closed means done or abandoned; everything else is still somebody's job, which
  // is why the page opens on it.
  const isClosed = (t) => ['closed', 'cancelled'].includes(t.status);
  const tabCounts = { all: all.length, open: all.filter((t) => !isClosed(t)).length, closed: all.filter(isClosed).length };
  const shown = tab === 'all' ? all : all.filter((t) => (tab === 'closed' ? isClosed(t) : !isClosed(t)));
  const counts = isReq ? shown.reduce((a, t) => { if (t.fulfilment) a[t.fulfilment] = (a[t.fulfilment] || 0) + 1; return a; }, {}) : {};

  // The same documents rolled up per product: what the warehouse actually has to
  // pick, instead of which pieces of paper the demand is spread across.
  const itemRows = useMemo(() => {
    const by = {};
    for (const t of shown) {
      for (const it of (t.items || [])) {
        const key = it.itemType + '|' + it.itemId + '|' + (it.variantId || 0);
        const row = by[key] || (by[key] = {
          key, itemId: it.itemId, variantId: it.variantId || 0,
          label: itemLabel[it.variantId ? 'v' + it.variantId : 'p' + it.itemId] || ('#' + it.itemId),
          unit: it.unit, requested: 0, sent: 0, received: 0, toKitchen: 0, docs: [], places: {},
        });
        const asked = Number(it.qtyRequested || it.qtySent || 0);
        row.requested += asked;
        row.sent += Number(isReq ? (it.qtyFulfilled || 0) : (it.qtySent || 0));
        row.received += Number(it.qtyReceived || 0);
        row.toKitchen += Number(it.qtyToKitchen || 0);
        const place = (isReq ? t.toLocation && t.toLocation.name : t.fromLocation && t.fromLocation.name) || '—';
        row.places[place] = (row.places[place] || 0) + asked;
        if (!row.docs.includes(t.code)) row.docs.push(t.code);
      }
    }
    const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
    return Object.values(by)
      .map((r) => ({
        ...r,
        requested: r3(r.requested), sent: r3(r.sent), received: r3(r.received), toKitchen: r3(r.toKitchen),
        outstanding: Math.max(0, r3(r.requested - r.sent)),
        placesLabel: Object.entries(r.places).map(([n, q]) => n + ' ' + num(q)).join(' · '),
      }))
      .sort((a, b) => b.outstanding - a.outstanding || a.label.localeCompare(b.label));
  }, [shown, itemLabel, isReq]);   // eslint-disable-line react-hooks/exhaustive-deps

  const list = useDataTable(shown, {
    searchPlaceholder: isReq ? 'Search request code, branch…' : 'Search code, location…',
    searchKeys: ['code', (t) => t.fromLocation?.name, (t) => t.toLocation?.name, 'status'],
    filters: [
      { key: 'status', label: 'Status', value: (t) => t.status },
      ...(isReq ? [{ key: 'fulfilment', label: 'Fulfilment', value: (t) => (t.fulfilment && FULFILMENT[t.fulfilment] ? FULFILMENT[t.fulfilment].label : '') }] : []),
      { key: 'from', label: 'From', value: (t) => t.fromLocation?.name },
      { key: 'to', label: 'To', value: (t) => t.toLocation?.name },
    ],
  });

  const itemList = useDataTable(itemRows, {
    searchPlaceholder: 'Search product…',
    searchKeys: ['label', 'placesLabel', (r) => r.docs.join(' ')],
  });
  const activeTable = view === 'items' ? itemList : list;

  return (
    <div>
      <div className="flex gap" style={{ marginBottom: '.7rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {[['open', 'Open'], ['closed', 'Closed'], ['all', 'All']].map(([v, label]) => (
          <button key={v} className={`pill-btn ${tab === v ? 'active' : ''}`} onClick={() => setTab(v)}>
            {label} ({tabCounts[v]})
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button className={`pill-btn ${view === 'docs' ? 'active' : ''}`} onClick={() => setView('docs')}>{isReq ? 'By request' : 'By transfer'}</button>
        <button className={`pill-btn ${view === 'items' ? 'active' : ''}`} onClick={() => setView('items')}>By product</button>
      </div>
      <TableToolbar dt={activeTable} right={<>
        {isReq && canFulfilReq && <button className="btn btn-gold" onClick={() => setConsole(true)}>⇪ Dispatch to branch</button>}
        {isReq
          ? can('transfers:request') && <button className="btn btn-primary" onClick={() => setRequesting(true)}>+ Request stock</button>
          : can('transfers:write') && <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New Transfer</button>}
      </>} />
      {isReq && (counts.waiting || counts.production || counts.ready) ? (
        <div className="flex gap" style={{ margin: '-.2rem 0 .8rem', flexWrap: 'wrap' }}>
          {counts.waiting ? <span className="badge badge-red">{counts.waiting} waiting in warehouse</span> : null}
          {counts.production ? <span className="badge badge-gold">{counts.production} in production</span> : null}
          {counts.ready ? <span className="badge badge-green">{counts.ready} ready to dispatch</span> : null}
        </div>
      ) : null}
      {loading ? <div className="spinner" /> : view === 'items' ? (<>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr>
              <th>Product</th>
              <th className="right">{isReq ? 'Requested' : 'Sent'}</th>
              {isReq && <th className="right">Dispatched</th>}
              <th className="right">Received</th>
              {isReq && <th className="right">Outstanding</th>}
              <th>{isReq ? 'Branches asking' : 'From'}</th>
              <th className="right">{isReq ? 'Requests' : 'Transfers'}</th>
            </tr></thead>
            <tbody>
              {itemList.rows.map((r) => (
                <tr key={r.key}>
                  <td><strong>{r.label}</strong></td>
                  <td className="right">{num(r.requested)} <span className="muted small">{r.unit}</span></td>
                  {isReq && <td className="right">{r.sent ? num(r.sent) : '—'}</td>}
                  <td className="right">{r.received ? num(r.received) : '—'}</td>
                  {isReq && (
                    <td className="right">
                      <strong className={r.outstanding > 0 ? 'neg' : 'muted'}>{r.outstanding ? num(r.outstanding) : '—'}</strong>
                      {r.toKitchen > 0 && <div className="muted small">{num(r.toKitchen)} to kitchen</div>}
                    </td>
                  )}
                  <td className="muted small">{r.placesLabel}</td>
                  <td className="right muted small" title={r.docs.join(', ')}>{r.docs.length}</td>
                </tr>
              ))}
              {itemList.rows.length === 0 && (
                <tr><td colSpan={isReq ? 7 : 5} className="empty">{itemList.isFiltered ? 'No products match.' : 'Nothing on these documents.'}</td></tr>
              )}
            </tbody>
            {itemList.rows.length > 0 && (
              <tfoot><tr className="total-row">
                <td><strong>Total — {itemList.rows.length} product{itemList.rows.length === 1 ? '' : 's'}</strong></td>
                <td className="right">{num(itemList.rows.reduce((a, r) => a + r.requested, 0))}</td>
                {isReq && <td className="right">{num(itemList.rows.reduce((a, r) => a + r.sent, 0))}</td>}
                <td className="right">{num(itemList.rows.reduce((a, r) => a + r.received, 0))}</td>
                {isReq && <td className="right"><strong>{num(itemList.rows.reduce((a, r) => a + r.outstanding, 0))}</strong></td>}
                <td colSpan={2}></td>
              </tr></tfoot>
            )}
          </table>
        </div>
        <TablePager dt={itemList} />
      </>) : (<>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Code</th><th>{isReq ? 'From (store)' : 'From'}</th><th>{isReq ? 'To (branch)' : 'To'}</th><th className="right">Products</th>{isReq && <th className="right">Needed by</th>}{isReq && <th>Fulfilment</th>}<th>Status</th><th className="right">Created</th><th></th></tr></thead>
            <tbody>
              {list.rows.map((t) => (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(t)} title="Open">
                  <td className="mono">{t.code}</td>
                  <td>{t.fromLocation?.name}</td>
                  <td>{t.toLocation?.name}</td>
                  <td className="right">{(t.items || []).length}</td>
                  {isReq && <td className="muted right">{t.needByDate ? day(t.needByDate) : '—'}</td>}
                  {isReq && <td>{t.fulfilment && FULFILMENT[t.fulfilment] ? <span className={`badge ${FULFILMENT[t.fulfilment].cls}`}>{FULFILMENT[t.fulfilment].label}</span> : <span className="muted">—</span>}</td>}
                  <td><span className={`badge ${statusBadge(t.status)}`}>{t.status}</span></td>
                  <td className="muted right">{dt(t.createdAt)}</td>
                  <td className="muted" style={{ textAlign: 'right' }}>›</td>
                </tr>
              ))}
              {list.rows.length === 0 && <tr><td colSpan={isReq ? 9 : 7} className="empty">{list.isFiltered ? 'No matches.' : (isReq ? 'No stock requests yet.' : 'No transfers yet.')}</td></tr>}
            </tbody>
          </table>
        </div>
        <TablePager dt={list} />
      </>)}
      {creating && <NewTransferModal products={products} locations={locations} onClose={() => setCreating(false)} onSaved={upsert} />}
      {requesting && <RequestModal products={products} locations={locations} onClose={() => setRequesting(false)} onSaved={upsert} />}
      {detail && <DetailModal transfer={detail} itemLabel={itemLabel} can={can} onClose={() => setDetail(null)} onChanged={upsert} />}
      {console_ && <DispatchConsole onClose={() => setConsole(false)} onDone={load} />}
    </div>
  );
};

const Transfers = () => <TransferList mode="transfers" />;
export const StockRequests = () => <TransferList mode="requests" />;

export default Transfers;
